import React, { useState, useEffect } from 'react';
import emailService from '../services/emailService';

const EmailAnalysisModal = ({ isOpen, onClose, emailId }) => {
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && emailId) {
      fetchAnalysisDetails();
    }
  }, [isOpen, emailId]);

  const fetchAnalysisDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await emailService.getEmailAnalysisDetails(emailId);
      setAnalysisDetails(details);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des détails');
      console.error('Erreur récupération détails analyse:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAnalysisResult = (result) => {
    if (!result) return 'Aucune donnée disponible';
    
    if (typeof result === 'string') {
      return result;
    }
    
    if (typeof result === 'object') {
      return JSON.stringify(result, null, 2);
    }
    
    return String(result);
  };

  const getAnalysisTypeLabel = (type) => {
    const labels = {
      'sentiment': 'Analyse de Sentiment',
      'entities': 'Entités Extraites',
      'summary': 'Résumé',
      'classification': 'Classification',
      'topics': 'Sujets Principaux',
      'key_points': 'Points Clés',
      'action_items': 'Actions Requises',
      'risk_assessment': 'Évaluation des Risques'
    };
    
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Détails de l'Analyse - Email #{emailId}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Chargement des détails de l'analyse...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {analysisDetails && !loading && (
            <div className="space-y-6">
              {/* Informations de l'email */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Informations de l'Email</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Sujet:</span>
                    <p className="text-gray-700">{analysisDetails.email_subject || 'Non spécifié'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Expéditeur:</span>
                    <p className="text-gray-700">{analysisDetails.email_sender || 'Non spécifié'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Date:</span>
                    <p className="text-gray-700">
                      {analysisDetails.email_date ? new Date(analysisDetails.email_date).toLocaleString('fr-FR') : 'Non spécifié'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">ID:</span>
                    <p className="text-gray-700">{analysisDetails.email_id}</p>
                  </div>
                </div>
              </div>

              {/* Analyses disponibles */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Analyses Disponibles</h3>
                {Object.keys(analysisDetails.analyses).length === 0 ? (
                  <p className="text-gray-500 italic">Aucune analyse disponible pour cet email</p>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(analysisDetails.analyses).map(([analysisType, analysisData]) => (
                      <div key={analysisType} className="border rounded-lg overflow-hidden">
                        <div className="bg-blue-50 px-4 py-2 border-b">
                          <h4 className="font-medium text-blue-900">
                            {getAnalysisTypeLabel(analysisType)}
                          </h4>
                          <div className="text-xs text-blue-600">
                            Créé le: {analysisData.created_at ? new Date(analysisData.created_at).toLocaleString('fr-FR') : 'Non spécifié'}
                          </div>
                        </div>
                        <div className="p-4">
                          {analysisData.result && Object.keys(analysisData.result).length > 0 ? (
                            <div className="space-y-3">
                              {Object.entries(analysisData.result).map(([key, value]) => (
                                <div key={key} className="border-l-4 border-blue-200 pl-3">
                                  <span className="font-medium text-gray-700 capitalize">
                                    {key.replace(/_/g, ' ')}:
                                  </span>
                                  <div className="mt-1 text-gray-600 whitespace-pre-wrap">
                                    {formatAnalysisResult(value)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">Aucun résultat d'analyse disponible</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Résumé des analyses */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-green-900 mb-2">Résumé des Analyses</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Object.keys(analysisDetails.analyses).length}
                    </div>
                    <div className="text-green-800">Types d'analyse</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Object.values(analysisDetails.analyses).filter(a => a.result && Object.keys(a.result).length > 0).length}
                    </div>
                    <div className="text-green-800">Analyses avec données</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {new Date(analysisDetails.analyses[Object.keys(analysisDetails.analyses)[0]]?.created_at || analysisDetails.email_date).toLocaleDateString('fr-FR')}
                    </div>
                    <div className="text-green-800">Date de la dernière analyse</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailAnalysisModal;